/**
 * Held things that are models rather than drawings.
 *
 * A few items have a modelled form - a sabre off Meshy, blocky enough to
 * pass for ours - kept as GLB under public/models and loaded once, up
 * front. Everything that puts a thing in a hand or on the ground asks
 * `heldModel(id)` first and falls back to the extruded drawing, so an
 * item with no model, and every item until the models have arrived,
 * draws as it always did. "Заміни текстуру шаблі на цю."
 *
 * Unlit, the base colour alone, the way every drawn thing is: a lit
 * material on one item beside flat-shaded everything else would look
 * like a photograph glued to a painting.
 *
 * Each model is stood the way a drawing is - the grip at the origin, the
 * business end up and to the right - so the same turns and scales that
 * place a drawing place it too, and nothing tuned by eye moves.
 */

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { PlainItemId } from "../core/block/items.js";

/**
 * Which items have a model, where it is, and how far to turn it about the
 * line of sight so that it lies a drawing's way, hilt bottom-left and tip
 * top-right: a model that comes hilt bottom-right and tip top-left needs a
 * quarter turn, one that stands upright an eighth.
 */
const MODELS: Readonly<Record<number, { url: string; turn: number; grow?: number }>> = {
  [PlainItemId.IronSabre]: { url: "/models/sabre.glb", turn: -Math.PI / 2 },
  // "Ось об'єкт для дерев'яної шаблі, зроби те саме, що для залізної."
  [PlainItemId.WoodenSabre]: { url: "/models/wooden-sabre.glb", turn: -Math.PI / 2 },
  // "Тепер те саме з адамантовою шаблею." This one stands hilt down, and
  // is a narrow thing: at a drawing's reach it came out a size smaller
  // than the other two, so it is grown to match them.
  [PlainItemId.AdamantSabre]: { url: "/models/adamant-sabre.glb", turn: -Math.PI / 4, grow: 1.18 },
};

/** How long a drawing's diagonal is, in the units a held thing is drawn at. */
const DRAWING_REACH = 1.24;

const loaded = new Map<number, THREE.Group>();
let generation = 0;
let loading: Promise<void> | null = null;

/**
 * Bring every model in. Once; the promise is shared. Any model that will
 * not load is left out, and the item keeps its drawing.
 */
export function preloadHeldModels(): Promise<void> {
  loading ??= Promise.all(Object.entries(MODELS).map(([id, { url, turn, grow }]) =>
    new Promise<void>((resolve) => {
      new GLTFLoader().load(url, (gltf) => {
        loaded.set(Number(id), stand(gltf.scene, turn, grow ?? 1));
        generation++;
        resolve();
      }, undefined, () => resolve());
    }))).then(() => undefined);
  return loading;
}

/** Goes up each time a model arrives; a hand that remembers it can refresh. */
export function heldModelGeneration(): number {
  return generation;
}

/** A fresh copy of the item's model, or null if it has none (yet). */
export function heldModel(id: number): THREE.Group | null {
  const model = loaded.get(id);
  return model === null || model === undefined ? null : model.clone();
}

/** Whether the item is one that has a model at all, loaded or not. */
export function hasHeldModel(id: number): boolean {
  return MODELS[id] !== undefined;
}

/**
 * Stand a model the way a drawing stands.
 *
 * Unlit; turned so the hilt is bottom-left and the tip top-right, which is
 * a drawing's way up; scaled so its diagonal is a drawing's; and moved so
 * the grip - a little way in from the bottom-left corner, as `gripOf`
 * finds one - is at the origin.
 */
function stand(scene: THREE.Group, turn: number, grow: number): THREE.Group {
  scene.traverse((o) => {
    if (o instanceof THREE.Mesh) {
      const was = o.material as THREE.MeshStandardMaterial;
      if (was.map !== null && was.map !== undefined) {
        // Nearest, like every texture in the game: the loader's linear
        // filter and mipmaps blur a voxel atlas's edges the moment the
        // thing is drawn bigger than its texels, which in the corner of
        // the eye it always is.
        was.map.magFilter = THREE.NearestFilter;
        was.map.minFilter = THREE.NearestFilter;
        was.map.generateMipmaps = false;
        was.map.needsUpdate = true;
      }
      o.material = new THREE.MeshBasicMaterial({
        map: was.map ?? null, color: was.color ?? new THREE.Color(0xffffff),
        // A rebuilt voxel model carries its colours on its vertices.
        vertexColors: o.geometry.hasAttribute("color"),
        side: THREE.DoubleSide,
      });
    }
  });
  const group = new THREE.Group();
  /*
   * The first sabre came hilt bottom-right, tip top-left. Half a turn
   * about the upright put the hilt bottom-left, the drawing's way; then it
   * was to be mirrored - "віддзеркаль її на 180 по Y" - keeping the hilt
   * where it is and the tip where it is, so the blade's curve bends the
   * other way and the other face shows. Both together are one thing: a
   * mirror through the picture and a turn about the line of sight, and
   * the turn is the model's own.
   */
  scene.rotation.set(0, 0, turn);
  scene.scale.set(1, 1, -1);
  group.add(scene);
  group.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(group);
  const size = box.getSize(new THREE.Vector3());
  const scale = DRAWING_REACH * grow / Math.hypot(size.x, size.y);
  scene.scale.set(scale, scale, -scale);
  group.updateMatrixWorld(true);
  const scaled = new THREE.Box3().setFromObject(group);
  const along = 0.18;
  const grip = new THREE.Vector3(
    scaled.min.x + (scaled.max.x - scaled.min.x) / 2 * along,
    scaled.min.y + (scaled.max.y - scaled.min.y) / 2 * along,
    (scaled.min.z + scaled.max.z) / 2,
  );
  scene.position.sub(grip);
  group.userData.sharedGeometry = true;
  return group;
}
